import React from 'react';
import {
  IconToggle,
  ImageWithPlaceholder,
  MarkdownText,
  Modal,
  AttributionBadge,
  InfoTooltip,
} from 'components';
import { usePatchChildProduct } from 'api/products/hooks';
import { Button, Image } from 'react-bootstrap';
import { FaEye, FaEyeSlash, FaTimesCircle } from 'react-icons/fa';
import { FiDownload, FiPlay } from 'react-icons/fi';
import { MdCheckCircle } from 'react-icons/md';
import { toastr } from 'utils';
import { formatDisplayDate, formatDisplayDateTime } from 'utils/date';
import history from 'utils/history';
import { ORDER_STATUS } from 'constants/orderStatus';
import RetailerStatusBadge, {
  getRetailerStatusColor,
} from 'pages/Retailer/components/RetailerStatusBadge/RetailerStatusBadge';
import Placeholder from '../assets/img/covers/placeholder.jpg';
import i18n from '../i18n';
import { WIDGET_EDIT, SUPER_USER_REGION_DETAIL, DASHBOARD } from './routes';

const ChildProductVisibilityCell = ({ childProduct }) => {
  const { mutate: updateChildProduct, isLoading } = usePatchChildProduct();

  const handleChange = (e) =>
    updateChildProduct(
      { uid: childProduct.uid, is_hidden: e.target.checked },
      { onError: (error) => toastr.error(`Failed to update visibility: ${error.message}`) },
    );

  const toggle = (
    <IconToggle
      checked={childProduct.is_hidden || childProduct.is_hidden_shopify || false}
      onChange={handleChange}
      disabled={isLoading || childProduct.is_hidden_shopify}
      leftIcon={<FaEyeSlash />}
      rightIcon={<FaEye />}
    />
  );

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {childProduct.is_hidden_shopify ? (
        <InfoTooltip title={i18n.t('pages:childProducts.shopifyHiddenTooltip')} placement="top">
          <div>{toggle}</div>
        </InfoTooltip>
      ) : (
        toggle
      )}
    </div>
  );
};

const formatDate = (cellValue, withTime = false) => {
  if (!cellValue) {
    return null;
  }
  return withTime ? formatDisplayDateTime(cellValue, null) : formatDisplayDate(cellValue, null);
};

const contentManagerBannerPreviewFrameStyle = {
  width: '100%',
  maxWidth: 280,
  aspectRatio: '16 / 6',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  borderRadius: 6,
  background: '#f5f7fa',
  border: '1px solid #e2e8f0',
};

const getOrderStatusBadgeInfo = (status) => {
  switch (status) {
    case ORDER_STATUS.PAID:
      return {
        backgroundColor: '#2E7D32',
        text: i18n.t('pages:orders.paid'),
      };
    case ORDER_STATUS.AUTHORIZED:
    case ORDER_STATUS.PENDING:
    case ORDER_STATUS.PARTIALLY_PAID:
      return {
        backgroundColor: '#C46A00',
        text: i18n.t(
          status === ORDER_STATUS.AUTHORIZED
            ? 'pages:orders.authorized'
            : status === ORDER_STATUS.PENDING
              ? 'pages:orders.pending'
              : 'pages:orders.partiallyPaid',
        ),
      };
    case ORDER_STATUS.REFUNDED:
    case ORDER_STATUS.PARTIALLY_REFUNDED:
    case ORDER_STATUS.VOIDED:
      return {
        backgroundColor: '#FF3838',
        text: i18n.t(
          status === ORDER_STATUS.REFUNDED
            ? 'pages:orders.refunded'
            : status === ORDER_STATUS.PARTIALLY_REFUNDED
              ? 'pages:orders.partiallyRefunded'
              : 'pages:orders.voided',
        ),
      };
    default:
      return {
        backgroundColor: '#9E2A2F',
        text: i18n.t('pages:orders.unknown'),
      };
  }
};

const TruncatedDescriptionCell = ({ value }) => {
  const description = value ? String(value) : '';
  const isLongDescription = description.length > 160;
  const preview = (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 5,
        overflow: 'hidden',
        textAlign: 'center',
        textOverflow: 'ellipsis',
        overflowWrap: 'anywhere',
        whiteSpace: 'normal',
      }}
    >
      {description ? <MarkdownText>{description}</MarkdownText> : '—'}
    </div>
  );

  return (
    <div>
      {isLongDescription ? (
        <div>
          {preview}
          <div style={{ marginTop: 4, textAlign: 'center' }}>
            <Modal
              header={{ title: i18n.t('app:description') }}
              contentStyle={{ width: 560, maxWidth: 'calc(100vw - 32px)' }}
              bodyStyle={{ maxHeight: '55vh', overflowY: 'auto' }}
              trigger={
                <Button
                  variant="link"
                  className="p-0"
                  style={{ fontSize: 12, lineHeight: 1.2, whiteSpace: 'nowrap' }}
                >
                  {i18n.t('app:showMore')}
                </Button>
              }
            >
              <MarkdownText>{description}</MarkdownText>
            </Modal>
          </div>
        </div>
      ) : (
        preview
      )}
    </div>
  );
};

const columns = {
  events: [
    {
      Header: 'name',
      accessor: 'name',
      centered: true,
      width: '150px',
    },
    {
      Header: i18n.t('app:description'),
      accessor: 'description',
      centered: true,
      width: '150px',
      renderCell: (cell) => (
        <p
          className="m-0"
          // eslint-disable-next-line
          dangerouslySetInnerHTML={{ __html: cell.row.original.description ?? '-' }}
        />
      ),
    },
    {
      Header: i18n.t('app:date'),
      accessor: 'date',
      centered: true,
      width: '150px',
      renderCell: (cell) => formatDate(cell.value, true),
    },
    {
      Header: i18n.t('app:shareLinkOnRetailerEventsTabShortcut'),
      accessor: 'is_shareable_on_retailer_page',
      centered: true,
      width: '100px',
      renderCell: (cell) => (
        <span
          className="px-4 py-2 text-uppercase"
          style={{
            fontSize: '0.6rem',
            fontWeight: 'bold',
            display: 'inline-block',
            backgroundColor: cell.value ? '#2E7D32' : '#9E2A2F',
            color: 'white',
            borderRadius: 4,
          }}
        >
          {cell.value ? i18n.t('app:yes') : i18n.t('app:no')}
        </span>
      ),
    },
    {
      Header: i18n.t('app:requireAuthForCommentsShortcut'),
      accessor: 'is_auth_required_for_comments',
      centered: true,
      width: '130px',
      renderCell: (cell) => (
        <span
          className="px-4 py-2 text-uppercase"
          style={{
            fontSize: '0.6rem',
            fontWeight: 'bold',
            display: 'inline-block',
            backgroundColor: cell.value ? '#2E7D32' : '#9E2A2F',
            color: 'white',
            borderRadius: 4,
          }}
        >
          {cell.value ? i18n.t('app:yes') : i18n.t('app:no')}
        </span>
      ),
    },
    {
      Header: i18n.t('app:requireAuthToJoinShortcut'),
      accessor: 'is_auth_required_to_join',
      centered: true,
      width: '100px',
      renderCell: (cell) => (
        <span
          className="px-4 py-2 text-uppercase"
          style={{
            fontSize: '0.6rem',
            fontWeight: 'bold',
            display: 'inline-block',
            backgroundColor: cell.value ? '#2E7D32' : '#9E2A2F',
            color: 'white',
            borderRadius: 4,
          }}
        >
          {cell.value ? i18n.t('app:yes') : i18n.t('app:no')}
        </span>
      ),
    },
    {
      Header: i18n.t('app:preview'),
      accessor: 'public_url',
      centered: true,
      sortable: false,
      width: '100px',
      renderCell: (cell) => (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <a target="_blank" className="btn btn-link px-1" href={cell.value}>
          <i className="fe fe-external-link" style={{ fontSize: 20 }} />
        </a>
      ),
    },
  ],
  adminEvents: (onPreview, onDownload) => [
    {
      Header: i18n.t('app:name'),
      accessor: 'name',
      centered: true,
      width: '220px',
    },
    {
      Header: i18n.t('app:creator'),
      accessor: 'creatorName',
      centered: true,
      width: '200px',
      renderCell: (cell) => (
        <Button variant="link" className="p-0" onClick={() => history.push(`${DASHBOARD}`)}>
          {cell.value || '-'}
        </Button>
      ),
    },
    {
      Header: i18n.t('app:startDate'),
      accessor: 'startDate',
      centered: true,
      width: '180px',
      renderCell: (cell) => formatDate(cell.value, true),
    },
    {
      Header: i18n.t('ownerPortal:events.columnDuration'),
      accessor: 'total_minutes_streamed',
      centered: true,
      width: '100px',
    },
    {
      Header: i18n.t('ownerPortal:events.columnSoldItems'),
      accessor: 'soldItems',
      centered: true,
      width: '100px',
    },
    {
      Header: i18n.t('app:preview'),
      accessor: 'previewUrl',
      centered: true,
      sortable: false,
      width: '100px',
      renderCell: (cell) => (
        <Button
          variant="link"
          className=" px-1"
          disabled={!cell.value}
          onClick={() => onPreview(cell.value, cell.row.original.name)}
          aria-label={i18n.t('app:preview')}
        >
          <FiPlay size={20} style={{ paddingRight: 0 }} />
        </Button>
      ),
    },
    {
      Header: i18n.t('ownerPortal:events.columnDownload'),
      accessor: 'downloadUrl',
      centered: true,
      sortable: false,
      width: '120px',
      renderCell: (cell) => (
        <Button
          variant="link"
          className="px-1"
          disabled={!cell.value}
          onClick={() => onDownload(cell.row.original)}
          aria-label={i18n.t('ownerPortal:events.columnDownload')}
        >
          <FiDownload size={20} style={{ paddingRight: 0 }} />
        </Button>
      ),
    },
  ],
  parent_product: [
    {
      id: 'name',
      Header: i18n.t('app:name'),
      accessor: 'name',
      centered: true,
      width: '200px',
    },
    {
      id: 'description',
      Header: i18n.t('app:description'),
      accessor: 'description',
      sortable: false,
      centered: true,
      width: '360px',
      renderCell: (cell) => <TruncatedDescriptionCell value={cell.value} />,
    },
    {
      id: 'category',
      Header: i18n.t('app:category'),
      accessor: 'category.name',
      centered: true,
      width: '150px',
    },
    {
      id: 'is_hidden',
      Header: i18n.t('app:hidden'),
      accessor: 'is_hidden_shopify',
      sortable: false,
      centered: true,
      width: '80px',
      hidden: true,
      renderCell: (cell) => (
        <InfoTooltip title={i18n.t('pages:childProducts.shopifyHiddenTooltip')} placement="top">
          <div>
            <span
              className="px-4 py-2 text-uppercase"
              style={{
                fontSize: '0.6rem',
                fontWeight: 'bold',
                display: 'inline-block',
                backgroundColor: cell.value ? '#9E2A2F' : '#2E7D32',
                color: 'white',
                borderRadius: 4,
              }}
            >
              {cell.value ? i18n.t('app:yes') : i18n.t('app:no')}
            </span>
          </div>
        </InfoTooltip>
      ),
    },
    {
      accessor: 'include_hidden',
      hidden: true,
    },
  ],
  child_product: [
    {
      Header: i18n.t('app:product Code'),
      accessor: 'product_code',
      centered: true,
      width: '125px',
    },
    {
      id: 'parent_product',
      Header: i18n.t('app:parent Product'),
      accessor: 'parent_product.name',
      centered: true,
      width: '250px',
      // TODO: uncomment if there is a need to edit parent product from child product table
      // renderCell: cell => (
      //   <Sidebar
      //     trigger={
      //       <Button variant="link" className="text-center w-100">
      //         {cell.render('Cell')}
      //       </Button>
      //     }
      //     header={{
      //       title: i18n.t('pages:parentProducts.updateParentProduct'),
      //       subTitle: i18n.t('pages:parentProducts.parentProduct'),
      //     }}
      //     sidebar
      //   >
      //     <EntityForm
      //       initialValues={cell.row.original.parent_product}
      //       entity="parent_product"
      //       type="update"
      //     />
      //   </Sidebar>
      // ),
    },
    {
      id: 'description',
      Header: i18n.t('app:description'),
      accessor: 'description',
      sortable: false,
      centered: true,
      width: '360px',
      renderCell: (cell) => <TruncatedDescriptionCell value={cell.value} />,
    },
    {
      Header: i18n.t('app:price'),
      accessor: 'price',
      centered: true,
      width: '100px',
      renderCell: (cell) => {
        const { price, currency } = cell.row.original;

        if (price === null || price === undefined) {
          return <p className="m-0">–</p>;
        }

        return (
          <p className="m-0">
            {price} {currency || 'USD'}
          </p>
        );
      },
    },
    {
      Header: i18n.t('app:picture'),
      accessor: 'picture',
      sortable: false,
      centered: true,
      width: '125px',
      renderCell: (cell) => (
        <Image
          className="rounded shadow-sm"
          style={{ width: 100, height: 80, objectFit: 'cover' }}
          src={cell.row.original.picture || Placeholder}
        />
      ),
    },
    {
      Header: i18n.t('app:hidden'),
      accessor: 'is_hidden',
      sortable: false,
      centered: true,
      width: '80px',
      renderCell: (cell) => (
        <div style={{ display: 'inline-block', margin: '0 auto' }}>
          <ChildProductVisibilityCell childProduct={cell.row.original} />
        </div>
      ),
    },
    {
      accessor: 'include_hidden',
      hidden: true,
    },
  ],
  color: [
    {
      Header: i18n.t('app:name'),
      accessor: 'name',
      centered: true,
      width: '200px',
    },
    {
      Header: 'hex',
      accessor: 'hex_value',
      sortable: false,
      centered: true,
      width: '200px',
    },
    {
      Header: i18n.t('app:entities.color'),
      accessor: '',
      sortable: false,
      centered: true,
      width: '200px',
      renderCell: (data) => (
        <div
          className="rounded shadow-sm"
          style={{
            width: 32,
            height: 32,
            backgroundColor: data.row.original.hex_value,
            margin: '0 auto',
          }}
        />
      ),
    },
  ],
  size: [
    {
      Header: i18n.t('app:name'),
      accessor: 'name',
      centered: true,
      width: '85%',
    },
  ],
  category: [
    {
      Header: i18n.t('app:name'),
      accessor: 'name',
      centered: true,
      width: '85%',
    },
  ],
  inventory: [
    {
      Header: i18n.t('app:name'),
      accessor: 'name',
      centered: true,
      width: '85%',
    },
  ],
  retailers: (t) => [
    {
      id: 'picture',
      Header: i18n.t('app:picture'),
      accessor: 'picture',
      sortable: false,
      centered: true,
      renderCell: (cell) => (
        <ImageWithPlaceholder
          style={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '1px solid #e9ecef',
          }}
          src={cell.row.original.picture}
          alt=""
        />
      ),
    },
    {
      id: 'email',
      Header: t('retailers.email'),
      accessor: 'email',
      centered: true,
      renderCell: (cell) => <span style={{ whiteSpace: 'nowrap' }}>{cell.value}</span>,
    },
    {
      id: 'name',
      Header: t('retailers.tableName'),
      accessor: 'name',
      sortable: false,
      centered: true,
      width: '200px',
      renderCell: (cell) => {
        const { name, last_name: lastName } = cell.row.original;
        const fullName = [name, lastName].filter(Boolean).join(' ');
        return (
          <p className="m-0" style={{ whiteSpace: 'nowrap' }}>
            {fullName || '—'}
          </p>
        );
      },
    },
    {
      id: 'invitation_status',
      Header: i18n.t('columns:status'),
      accessor: 'invitation_status',
      centered: true,
      sortable: false,
      width: '150px',
      renderCell: (cell) => (
        <RetailerStatusBadge
          invitationStatus={cell.row.original.invitation_status}
          isBlocked={cell.row.original.is_blocked}
        />
      ),
    },
  ],

  locationUsers: [
    {
      id: 'email',
      Header: i18n.t('ownerPortal:locations.usersTab.email'),
      centered: true,
      accessor: (row) => {
        const user = row.admin || row.retailer;
        if (!user) return '';
        return user.email || '';
      },
    },
    {
      id: 'fullname',
      Header: i18n.t('ownerPortal:locations.usersTab.name'),
      centered: true,
      accessor: (row) => {
        const user = row.admin || row.retailer;
        if (!user) return '';
        return `${user.name ?? ''} ${user.last_name ?? ''}`.trim();
      },
    },
    {
      id: 'role',
      Header: i18n.t('ownerPortal:locations.usersTab.role'),
      centered: true,
      accessor: 'role',
    },
  ],
  look: [
    {
      Header: i18n.t('app:image'),
      sortable: false,
      accessor: 'pictures',
      renderCell: (cell) => (
        <Image
          className="rounded"
          style={{ width: '160px', height: '90px', objectFit: 'cover' }}
          src={cell.value[0] || Placeholder}
        />
      ),
    },
    {
      Header: 'id',
      accessor: 'created_at',
      hidden: true,
      renderCell: (cell) => new Date(cell.value).getTime(),
    },
    {
      Header: i18n.t('app:name'),
      accessor: 'name',
    },
    {
      Header: i18n.t('ownerPortal:Looks.enabled'),
      accessor: 'enabled',
      sortable: false,
      centered: true,
      renderCell: (cell) =>
        cell.value ? (
          <MdCheckCircle style={{ margin: '0 auto', display: 'block' }} size="20px" color="green" />
        ) : (
          <FaTimesCircle style={{ margin: '0 auto', display: 'block' }} size="18px" color="red" />
        ),
    },
    {
      Header: i18n.t('ownerPortal:Looks.visible'),
      accessor: 'is_visible',
      sortable: false,
      centered: true,
      renderCell: (cell) =>
        cell.value ? (
          <MdCheckCircle style={{ margin: '0 auto', display: 'block' }} size="20px" color="green" />
        ) : (
          <FaTimesCircle style={{ margin: '0 auto', display: 'block' }} size="18px" color="red" />
        ),
    },
    {
      Header: i18n.t('ownerPortal:Looks.draft'),
      accessor: 'is_draft',
      sortable: false,
      centered: true,
      renderCell: (cell) =>
        cell.value ? (
          <MdCheckCircle style={{ margin: '0 auto', display: 'block' }} size="20px" color="green" />
        ) : (
          <FaTimesCircle style={{ margin: '0 auto', display: 'block' }} size="18px" color="red" />
        ),
    },
    {
      Header: i18n.t('ownerPortal:Looks.productCount'),
      accessor: 'products_count',
      centered: true,
    },
    {
      Header: i18n.t('ownerPortal:Looks.viewsCount'),
      accessor: 'views_count',
      centered: true,
    },
  ],

  batchRetailer: [
    {
      Header: i18n.t('pages:retailers.name'),
      accessor: 'first_name',
      sortable: false,
    },
    {
      Header: i18n.t('pages:retailers.lastName'),
      accessor: 'last_name',
      sortable: false,
    },
    {
      Header: i18n.t('pages:retailers.email'),
      accessor: 'email',
      sortable: false,
    },
    {
      Header: i18n.t('pages:retailers.inviteToMobileApp'),
      accessor: 'invite',
      sortable: false,
      renderCell: (cell) => <p className="ml-4">{cell.value?.length ? '✅' : '❌'}</p>,
    },
  ],
  order: [
    {
      Header: i18n.t('pages:orders.picture'),
      accessor: 'child_product_picture_url',
      sortable: false,
      centered: true,
      renderCell: (cell) => (
        <Image
          className="rounded shadow-sm"
          style={{ width: 100, height: 80, objectFit: 'cover' }}
          src={cell.value || Placeholder}
        />
      ),
    },
    {
      Header: i18n.t('pages:orders.productName'),
      accessor: 'name',
      centered: true,
    },
    {
      id: 'attribution',
      Header: i18n.t('pages:orders.attributions'),
      accessor: (row) => ({
        attributed_retailer: row.attributed_retailer,
        attributed_location: row.attributed_location,
      }),
      centered: true,
      sortable: false,
      renderCell: (cell) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {cell.value.attributed_retailer && (
            <AttributionBadge
              attributedSource={cell.value.attributed_retailer}
              sourceType="retailer"
            />
          )}
          {cell.value.attributed_location && (
            <AttributionBadge
              attributedSource={cell.value.attributed_location}
              sourceType="location"
            />
          )}
          {!cell.value.attributed_retailer && !cell.value.attributed_location && <>&mdash;</>}
        </div>
      ),
    },
    {
      Header: i18n.t('pages:orders.quantity'),
      accessor: 'quantity',
      centered: true,
    },
    {
      Header: i18n.t('pages:orders.price'),
      accessor: 'price',
      centered: true,
      renderCell: (cell) => `$${Number.parseFloat(cell.value).toFixed(2)}`,
    },
  ],
  superUserRegions: [
    { Header: i18n.t('pages:superUser.regionName'), accessor: 'name', centered: true },
    {
      Header: i18n.t('pages:superUser.creator'),
      accessor: 'creator',
      centered: true,
      renderCell: (cell) => cell.value || '-',
    },
    { Header: i18n.t('pages:superUser.currency'), accessor: 'base_currency', centered: true },
    {
      Header: i18n.t('pages:superUser.activeRetailers'),
      accessor: 'active_retailers_count',
      centered: true,
    },
    {
      Header: i18n.t('pages:superUser.products'),
      accessor: 'child_products_count',
      centered: true,
    },
    {
      Header: i18n.t('pages:superUser.totalLooks'),
      accessor: 'total_looks_count',
      centered: true,
    },
    {
      Header: i18n.t('pages:superUser.totalRevenue'),
      accessor: 'total_revenue_usd',
      centered: true,
      renderCell: (cell) => `$${cell.value}`,
    },
    {
      Header: i18n.t('pages:superUser.regionFeed'),
      accessor: 'full_domain',
      centered: true,
      sortable: false,
      renderCell: (cell) =>
        cell.value ? (
          <a
            href={`https://${cell.value}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fe fe-external-link" style={{ fontSize: 20 }} />
          </a>
        ) : null,
    },
  ],
  superUserPlans: [
    { Header: i18n.t('pages:superUser.planName'), accessor: 'name', centered: true },
    { Header: i18n.t('pages:superUser.slug'), accessor: 'slug', centered: true },
    {
      Header: i18n.t('pages:superUser.price'),
      accessor: 'price',
      centered: true,
      renderCell: (cell) => `${cell.value} ${cell.row.original.currency}`,
    },
    { Header: i18n.t('pages:superUser.maxLocations'), accessor: 'max_locations', centered: true },
    { Header: i18n.t('pages:superUser.maxProducts'), accessor: 'max_products', centered: true },
    { Header: i18n.t('pages:superUser.maxRetailers'), accessor: 'max_retailers', centered: true },
    { Header: i18n.t('pages:superUser.maxEvents'), accessor: 'max_events', centered: true },
  ],
  get superUserRegionPlans() {
    return [
      ...this.superUserPlans,
      {
        Header: i18n.t('pages:superUser.region'),
        accessor: 'region',
        centered: true,
        sortable: false,
        renderCell: (cell) =>
          cell.value ? (
            <a
              href={SUPER_USER_REGION_DETAIL.replace(':uid', cell.value)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                history.push(SUPER_USER_REGION_DETAIL.replace(':uid', cell.value));
              }}
            >
              <i className="fe fe-external-link" style={{ fontSize: 20 }} />
            </a>
          ) : null,
      },
    ];
  },
  superUserSubscriptions: [
    { Header: i18n.t('pages:superUser.planName'), accessor: 'plan_name', centered: true },
    {
      Header: i18n.t('pages:superUser.startDate'),
      accessor: 'start_date',
      centered: true,
      renderCell: (cell) => formatDate(cell.value),
    },
    {
      Header: i18n.t('pages:superUser.endDate'),
      accessor: 'end_date',
      centered: true,
      renderCell: (cell) => formatDate(cell.value),
    },
    {
      Header: i18n.t('pages:superUser.paymentMethod'),
      accessor: 'payment_method',
      centered: true,
    },
    { Header: i18n.t('pages:superUser.maxLocations'), accessor: 'max_locations', centered: true },
    { Header: i18n.t('pages:superUser.maxProducts'), accessor: 'max_products', centered: true },
    { Header: i18n.t('pages:superUser.maxRetailers'), accessor: 'max_retailers', centered: true },
    { Header: i18n.t('pages:superUser.maxEvents'), accessor: 'max_events', centered: true },
  ],
  superUserAccess: [
    {
      Header: i18n.t('pages:superUser.userEmail'),
      accessor: 'email',
      centered: true,
      sortable: false,
    },
    {
      Header: i18n.t('pages:superUser.userName'),
      accessor: 'name',
      centered: true,
      sortable: false,
      renderCell: (cell) => {
        const { name, last_name } = cell.row.original;
        const full = [name, last_name].filter(Boolean).join(' ');
        return full || '-';
      },
    },
    {
      Header: i18n.t('pages:superUser.userRole'),
      accessor: 'role',
      centered: true,
      sortable: false,
      renderCell: (cell) => cell.value || '-',
    },
    {
      Header: i18n.t('pages:superUser.userLastLogin'),
      accessor: 'last_login',
      centered: true,
      sortable: false,
      renderCell: (cell) => formatDate(cell.value, true) || '-',
    },
  ],
  superUserImpersonation: (onImpersonate, currentUserUid) => [
    {
      Header: i18n.t('pages:superUser.userName'),
      accessor: 'name',
      centered: true,
      sortable: false,
      renderCell: (cell) => {
        const { name, last_name, is_test } = cell.row.original;
        const fullName = [name, last_name].filter(Boolean).join(' ') || '-';
        return (
          <span>
            {fullName}
            {is_test && (
              <span
                style={{
                  marginLeft: 6,
                  padding: '2px 6px',
                  borderRadius: 4,
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 600,
                  verticalAlign: 'middle',
                }}
              >
                TEST
              </span>
            )}
          </span>
        );
      },
    },
    {
      Header: i18n.t('pages:superUser.userEmail'),
      accessor: 'email',
      centered: true,
      sortable: false,
    },
    {
      Header: i18n.t('pages:superUser.profiles'),
      accessor: 'retailer_profiles',
      centered: true,
      sortable: false,
      renderCell: (cell) => {
        const { admin_profile, retailer_profiles } = cell.row.original;
        const pills = [];
        if (admin_profile) {
          pills.push({ label: `Admin · ${admin_profile.brand}`, color: '#1565C0' });
        }
        (retailer_profiles || []).forEach((p) => {
          pills.push({
            label: p.region,
            color: getRetailerStatusColor(p.invitation_status, false),
          });
        });
        if (pills.length === 0) return '-';
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            {pills.map((pill, idx) => (
              <span
                key={idx}
                style={{
                  padding: '2px 8px',
                  borderRadius: 4,
                  backgroundColor: pill.color,
                  color: 'white',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                }}
              >
                {pill.label}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      Header: i18n.t('pages:superUser.impersonateButton'),
      id: 'impersonate',
      accessor: 'uid',
      centered: true,
      sortable: false,
      width: '60px',
      renderCell: (cell) => {
        if (cell.row.original.uid === currentUserUid) return null;
        return (
          <FaEye
            size={18}
            style={{ cursor: 'pointer', color: '#1565C0' }}
            title={i18n.t('pages:superUser.impersonateButton')}
            onClick={(e) => {
              e.stopPropagation();
              onImpersonate(cell.row.original.uid, cell.row.original.email);
            }}
          />
        );
      },
    },
  ],
  orders: [
    {
      id: 'created_at',
      Header: i18n.t('pages:orders.date'),
      accessor: 'created_at',
      renderCell: (cell) => formatDate(cell.value, true),
      centered: true,
    },
    {
      id: 'total_price',
      Header: i18n.t('pages:orders.totalPrice'),
      accessor: 'total_price',
      renderCell: (cell) => `${cell.value} ${cell.row.original.currency}`,
      centered: true,
    },
    {
      id: 'financial_status',
      Header: i18n.t('pages:orders.status'),
      accessor: 'financial_status',
      centered: true,
      renderCell: (cell) => {
        const { backgroundColor, text } = getOrderStatusBadgeInfo(
          cell.row.original.financial_status,
        );
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 8px',
              margin: '0 auto',
              textTransform: 'uppercase',
              backgroundColor,
              color: 'white',
              borderRadius: 4,
              fontSize: 14,
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      id: 'is_placed_through_rambla',
      Header: i18n.t('pages:orders.source'),
      accessor: 'is_placed_through_rambla',
      sortable: false,
      centered: true,
      hidden: true,
      renderCell: (cell) => (
        <span className={`badge text-uppercase bg-${cell.value ? 'success' : 'secondary'}`}>
          {cell.value ? i18n.t('pages:orders.rambla') : i18n.t('pages:orders.nonRambla')}
        </span>
      ),
    },
    {
      accessor: 'include_non_rambla',
      hidden: true,
    },
  ],
  retailerSales: [
    {
      id: 'picture',
      Header: i18n.t('pages:retailerSales.picture'),
      accessor: 'product.picture',
      sortable: false,
      centered: true,
      renderCell: (cell) => (
        <Image
          className="rounded shadow-sm"
          style={{ width: 100, height: 80, objectFit: 'cover' }}
          src={cell.value || Placeholder}
        />
      ),
    },
    {
      id: 'product_name',
      Header: i18n.t('pages:retailerSales.productName'),
      accessor: 'product.parent_product_name',
      sortable: false,
      centered: true,
    },
    {
      id: 'product_code',
      Header: i18n.t('pages:retailerSales.productCode'),
      accessor: 'product.product_code',
      sortable: false,
      centered: true,
    },
    {
      id: 'quantity',
      Header: i18n.t('pages:retailerSales.quantity'),
      accessor: 'quantity',
      sortable: false,
      centered: true,
    },
    {
      id: 'price',
      Header: i18n.t('pages:retailerSales.unitPrice'),
      accessor: 'price',
      sortable: false,
      centered: true,
      renderCell: (cell) => `$${Number.parseFloat(cell.value).toFixed(2)}`,
    },
    {
      id: 'total_price',
      Header: i18n.t('pages:retailerSales.totalPrice'),
      accessor: (row) => Number.parseFloat(row.price) * row.quantity,
      sortable: false,
      centered: true,
      renderCell: (cell) => `$${cell.value.toFixed(2)}`,
    },
    {
      id: 'sold_at',
      Header: i18n.t('pages:retailerSales.soldAt'),
      accessor: 'sold_at',
      renderCell: (cell) => formatDate(cell.value, true),
      centered: true,
    },
  ],
  widgets: [
    {
      id: 'name',
      Header: i18n.t('pages:widgets.listName'),
      accessor: 'name',
      centered: true,
      renderCell: (cell) => (
        <Button
          variant="link"
          className="p-0"
          onClick={() => history.push(WIDGET_EDIT.replace(':id', cell.row.original.uid))}
        >
          {cell.value}
        </Button>
      ),
    },
    {
      id: 'type',
      Header: i18n.t('pages:widgets.listType'),
      accessor: 'type',
      centered: true,
      renderCell: (cell) => {
        const widgetTypeLabels = {
          looks: i18n.t('pages:widgets.types.look'),
          retailers: i18n.t('pages:widgets.types.retailers'),
          newest_retailers: i18n.t('pages:widgets.types.newRetailers'),
          top_retailers: i18n.t('pages:widgets.types.topRetailers'),
        };
        return (widgetTypeLabels[cell.value] || '—').toUpperCase();
      },
    },
  ],
  dashboardLooks: [
    {
      Header: i18n.t('pages:dashboard.regionLooks.table.columns.name'),
      accessor: 'name',
      centered: true,
      sortable: true,
    },
    {
      Header: i18n.t('pages:dashboard.regionLooks.table.columns.retailerName'),
      accessor: 'retailerName',
      centered: true,
      sortable: false,
    },
    {
      Header: i18n.t('pages:dashboard.regionLooks.table.columns.views'),
      accessor: 'views',
      centered: true,
      sortable: false,
    },
    {
      Header: i18n.t('pages:dashboard.regionLooks.table.columns.productsAddedToBag'),
      accessor: 'productsAddedToBag',
      centered: true,
      sortable: false,
    },
    {
      Header: i18n.t('pages:dashboard.regionLooks.table.columns.startedCarts'),
      accessor: 'startedCarts',
      centered: true,
      sortable: false,
    },
  ],
  dashboardCategories: [
    {
      Header: i18n.t('pages:dashboard.regionLooks.table.columns.categoryName'),
      accessor: 'name',
      centered: true,
      sortable: true,
    },
    {
      Header: i18n.t('pages:dashboard.regionLooks.table.columns.categoryProductsSold'),
      accessor: 'productsSold',
      centered: true,
      sortable: true,
    },
  ],
  dashboardProducts: [
    {
      Header: i18n.t('pages:dashboard.regionProducts.table.columns.productCode'),
      accessor: 'productCode',
      centered: true,
      sortable: true,
    },
    {
      Header: i18n.t('pages:dashboard.regionProducts.table.columns.parentProductName'),
      accessor: 'parentProductName',
      centered: true,
      sortable: true,
    },
    {
      Header: i18n.t('pages:dashboard.regionProducts.table.columns.price'),
      accessor: 'price',
      centered: true,
      sortable: true,
    },
    {
      Header: i18n.t('pages:dashboard.regionProducts.table.columns.views'),
      accessor: 'views',
      centered: true,
      sortable: false,
    },
    {
      Header: i18n.t('pages:dashboard.regionProducts.table.columns.addedToCart'),
      accessor: 'addedToCart',
      centered: true,
      sortable: false,
    },
    {
      Header: i18n.t('pages:dashboard.regionProducts.table.columns.sold'),
      accessor: 'sold',
      centered: true,
      sortable: true,
    },
  ],
  contentManagerBanners: () => [
    {
      Header: i18n.t('pages:contentManager.banners.image'),
      accessor: 'image_url',
      width: '320px',
      centered: true,
      sortable: false,
      renderCell: (cell) => (
        <div style={contentManagerBannerPreviewFrameStyle}>
          <img
            src={cell.row.original.image_url || Placeholder}
            alt={cell.row.original.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
      ),
    },
    {
      Header: i18n.t('pages:contentManager.banners.name'),
      accessor: 'name',
      centered: true,
      renderCell: (cell) => <p style={{ margin: 0, wordBreak: 'break-word' }}>{cell.value}</p>,
    },
  ],
};

export default columns;
